import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Tabs as T } from 'radix-ui';
import { type AnchorHTMLAttributes, type ComponentType, createContext, type ReactNode, useContext } from 'react';
import { cn } from '../lib/cn';
import { IconButton } from './button';
import { Select } from './select';

/* ------------------------------------------------------------ Link plumbing */

export type UILinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { href: string };
const DefaultLink: ComponentType<UILinkProps> = ({ href, ...props }) => <a href={href} {...props} />;
const LinkContext = createContext<ComponentType<UILinkProps>>(DefaultLink);

/** The desktop app supplies its router's Link here so design-system components stay router-agnostic. */
export function UILinkProvider({ link, children }: { link: ComponentType<UILinkProps>; children: ReactNode }) {
  return <LinkContext.Provider value={link}>{children}</LinkContext.Provider>;
}
export function useUILink(): ComponentType<UILinkProps> {
  return useContext(LinkContext);
}

/* -------------------------------------------------------------- Breadcrumbs */

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export function Breadcrumbs({ items, className }: { items: BreadcrumbItem[]; className?: string }) {
  const Link = useUILink();
  return (
    <nav aria-label="Breadcrumb" className={className}>
      <ol className="flex flex-wrap items-center gap-1 text-sm text-muted">
        {items.map((item, i) => {
          const last = i === items.length - 1;
          return (
            <li key={`${item.label}-${i}`} className="flex items-center gap-1">
              {item.href && !last ? (
                <Link href={item.href} className="rounded-sm text-muted no-underline hover:text-fg hover:underline">
                  {item.label}
                </Link>
              ) : (
                <span aria-current={last ? 'page' : undefined} className={last ? 'font-medium text-fg' : undefined}>
                  {item.label}
                </span>
              )}
              {!last ? <ChevronRight className="size-3.5 text-disabled" aria-hidden /> : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/* --------------------------------------------------------------- PageHeader */

export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
  meta,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  actions?: ReactNode;
  /** Small row under the title: badges, counts, last-updated. */
  meta?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cn('mb-6', className)}>
      {breadcrumbs?.length ? <Breadcrumbs items={breadcrumbs} className="mb-2" /> : null}
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold leading-9 text-fg">{title}</h1>
          {description ? <p className="mt-1 max-w-2xl text-base text-muted">{description}</p> : null}
          {meta ? <div className="mt-2 flex flex-wrap items-center gap-2">{meta}</div> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </header>
  );
}

/* ------------------------------------------------------------------ TabNav */

export interface TabNavItem {
  id: string;
  label: string;
  href: string;
  count?: number;
  icon?: ReactNode;
}

const tabBase =
  'relative inline-flex h-10 items-center gap-2 whitespace-nowrap px-3 text-base font-medium text-muted transition-colors hover:text-fg ' +
  'after:absolute after:inset-x-3 after:-bottom-px after:h-0.5 after:rounded-full after:bg-transparent after:transition-colors [&_svg]:size-4';
const tabActive = 'text-fg after:bg-brand';

/** Link-based tabs (URL-driven: deep-linkable, back button works). Athlete 360 and injury workspace use this. */
export function TabNav({ items, activeId, label, className }: { items: TabNavItem[]; activeId: string; label: string; className?: string }) {
  const Link = useUILink();
  return (
    <nav aria-label={label} className={cn('flex gap-1 overflow-x-auto border-b', className)}>
      {items.map((item) => {
        const active = item.id === activeId;
        return (
          <Link key={item.id} href={item.href} aria-current={active ? 'page' : undefined} className={cn(tabBase, 'no-underline', active && tabActive)}>
            {item.icon}
            {item.label}
            {item.count !== undefined ? (
              <span className="rounded-full bg-sunken px-1.5 text-xs font-medium tabular-nums text-muted">{item.count}</span>
            ) : null}
          </Link>
        );
      })}
    </nav>
  );
}

/* -------------------------------------------------------------------- Tabs */

export const Tabs = T.Root;
export const TabsContent = ({ className, ...props }: React.ComponentProps<typeof T.Content>) => (
  <T.Content className={cn('mt-5 outline-none focus-visible:outline-2 focus-visible:outline-offset-4', className)} {...props} />
);

export function TabsList({ className, ...props }: React.ComponentProps<typeof T.List>) {
  return <T.List className={cn('flex gap-1 overflow-x-auto border-b', className)} {...props} />;
}

export function TabsTrigger({ className, count, children, ...props }: React.ComponentProps<typeof T.Trigger> & { count?: number }) {
  return (
    <T.Trigger className={cn(tabBase, 'data-[state=active]:text-fg data-[state=active]:after:bg-brand', className)} {...props}>
      {children}
      {count !== undefined ? <span className="rounded-full bg-sunken px-1.5 text-xs font-medium tabular-nums text-muted">{count}</span> : null}
    </T.Trigger>
  );
}

/* -------------------------------------------------------------- Pagination */

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  noun = 'results',
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  noun?: string;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-1 py-3 text-sm text-muted">
      <p aria-live="polite">
        {total === 0 ? (
          `No ${noun}`
        ) : (
          <>
            Showing <span className="font-medium tabular-nums text-fg">{from}–{to}</span> of{' '}
            <span className="font-medium tabular-nums text-fg">{total}</span> {noun}
          </>
        )}
      </p>
      <div className="flex items-center gap-3">
        {onPageSizeChange ? (
          <div className="flex items-center gap-2">
            <span id="page-size-label">Rows</span>
            <Select
              aria-label="Rows per page"
              className="h-8 w-20"
              value={String(pageSize)}
              onValueChange={(v) => onPageSizeChange(Number(v))}
              options={pageSizeOptions.map((n) => ({ value: String(n), label: String(n) }))}
            />
          </div>
        ) : null}
        <div className="flex items-center gap-1">
          <IconButton label="Previous page" size="sm" variant="secondary" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
            <ChevronLeft />
          </IconButton>
          <span className="min-w-16 text-center tabular-nums">
            {page} / {pages}
          </span>
          <IconButton label="Next page" size="sm" variant="secondary" disabled={page >= pages} onClick={() => onPageChange(page + 1)}>
            <ChevronRight />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
